/**
 * ADD RELATION DRAWER — Modale per aggiungere relazioni inline.
 *
 * Due modalita' selezionabili via Tabs in cima:
 *   - "Aggiungi esistente": search entita' esistenti + collega
 *   - "Crea nuova": form di creazione inline (riusa PersonFormBody o
 *     BusinessFormBody), poi crea + collega atomicamente
 *
 * La modalita' "Crea nuova" e' disponibile solo per targetType
 * supportati (Person, Business). Per gli altri (Quotation, Task, ...)
 * i tab non vengono mostrati e il drawer si comporta come prima (solo
 * modalita' esistente).
 *
 * Il nome dice "Drawer" per ragioni storiche: era un pannello laterale, ora e'
 * una modale come le altre creazioni (stesse classi .app-dialog/.tc-head del
 * "Crea task"). Il nome resta perche' e' esportato da @crm/core e usato dai
 * plugin: rinominarlo vorrebbe dire toccarne il codice per un'estetica.
 */

import { useState, useEffect, useCallback, useRef, type UIEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from './useToast';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import { useServerPagedSearch, useScrollAutoFill } from '@crm/shared';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { createBidirectionalRelationship } from '../entities/relationship/relationshipThunks';
import { selectRelationshipsByTargetType } from '../entities/relationship/relationshipSlice';
import { createPerson } from '../entities/person/personThunks';
import { createBusiness } from '../entities/business/businessThunks';
import { PersonFormBody } from '../entities/person/PersonFormBody';
import { BusinessFormBody } from '../entities/business/BusinessFormBody';
import { getEntityType, getEntityLabelFromPayload, getEntityDescriptionFromPayload, resolveEntityLabel, getEntityFqn, extractEntityIdFromPayload, entityTypeLabel, entityTypeLabelPlural } from '../entityRegistry/entityRegistry';
import { useCurrentGroupId } from '../auth/authSelectors';
import type { PersonCreatePayload } from '../entities/person/personTypes';
import type { BusinessCreatePayload } from '../entities/business/businessTypes';

const PICKER_PAGE_SIZE = 20;
/** Distanza dal bottom (px) a cui triggerare il load-more. */
const SCROLL_THRESHOLD = 50;

interface AddRelationDrawerProps {
  sourceType: string;
  sourceId: number;
  targetType: string;
  onClose: () => void;
  onCreated: () => void;
  /** Valore iniziale della description "forward" (source -> target). */
  defaultDescForward?: string;
  /** Valore iniziale della description "reverse" (target -> source). */
  defaultDescReverse?: string;
  /**
   * Hook chiamato dopo ogni link source<->target creato con successo,
   * prima di onCreated. Serve ai wrapper che devono creare Relationship
   * aggiuntive per ogni target (es. plugin che collega anche entita'
   * derivate dal contesto).
   */
  onAfterLink?: (targetId: number) => void | Promise<void>;
  /**
   * Se fornito (e il targetType NON supporta la creazione inline), mostra un
   * tab "Crea" FINTO: cliccandolo non apre un form nel drawer ma esegue questa
   * callback (tipicamente naviga alla pagina di creazione dedicata).
   */
  onCreateNew?: () => void;
  /**
   * Se fornito, il drawer fa solo da SELETTORE: «Collega» passa gli id scelti a questa callback
   * invece di creare le relazioni, e i campi delle descrizioni non compaiono. Serve a chi deve
   * controllare e collegare da se', tutto insieme: «Collega a una pratica esistente» di Pollini
   * verifica permesso e collegabilita' e collega l'intera conversazione dal suo endpoint. Senza,
   * il drawer avrebbe gia' collegato il primo record prima dei suoi controlli. Se la callback
   * lancia, il drawer resta aperto e mostra il messaggio dell'errore.
   */
  onSelect?: (targetIds: number[]) => Promise<void>;
}

interface SearchResult {
  id: number;
  label: string;
  /** Seconda riga facoltativa (`getDescriptionFromPayload` del tipo): solo se valorizzata. */
  description?: string;
}

type Mode = 'existing' | 'new';

/** Target type per cui la modalita' "Crea nuova" e' supportata. */
const INLINE_CREATABLE_TYPES = new Set(['Person', 'Business']);

/** Id del form inline "Crea nuova" (Person o Business, uno alla volta). */
const FORM_ID = 'add-relation-create-form';

export function AddRelationDrawer({
  sourceType,
  sourceId,
  targetType,
  onClose,
  onCreated,
  defaultDescForward,
  defaultDescReverse,
  onAfterLink,
  onCreateNew,
  onSelect,
}: AddRelationDrawerProps) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const toast = useToast();
  const entityType = getEntityType(targetType);
  const groupId = useCurrentGroupId();

  const canCreateInline = INLINE_CREATABLE_TYPES.has(targetType);
  // Tab finto "Crea": targetType non creabile inline ma con callback di
  // navigazione (es. Quotation → pagina /quotations/new).
  const fakeCreateTab = !canCreateInline && !!onCreateNew;
  const showTabs = canCreateInline || fakeCreateTab;
  const [mode, setMode] = useState<Mode>('existing');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [descForward, setDescForward] = useState(defaultDescForward ?? '');
  const [descReverse, setDescReverse] = useState(defaultDescReverse ?? '');
  const [creating, setCreating] = useState(false);
  // Bloccato dal check di univocità email in PersonFormBody (solo tab "Crea
  // nuova" con targetType Person): submit "Crea e collega" disabilitato.
  const [personBlocked, setPersonBlocked] = useState(false);

  // byTargetType indicizzato per FQN: converte source/targetType short via registry.
  const sourceFqn = getEntityFqn(sourceType);
  const targetFqn = getEntityFqn(targetType);
  // Gia' collegati alla NOSTRA sorgente: serve a escluderli dai risultati di
  // ricerca. Letto dal bucket di (sourceFqn, sourceId), non dallo slice intero.
  const byTargetType = useAppSelector(selectRelationshipsByTargetType(sourceFqn, sourceId));
  const currentRelations = targetFqn ? byTargetType[targetFqn] ?? [] : [];
  const linkedIdsJson = JSON.stringify(currentRelations.map((r) => r.classPK2));

  // Avvisa se source/target non sono registrati: i handler escono silenziosi
  // altrimenti, senza segnale del bug al programmatore.
  useEffect(() => {
    if (!sourceFqn) {
      console.warn(
        '[relationship] AddRelationDrawer: sourceType non registrato nel registry',
        { sourceType }
      );
    }
    if (!targetFqn) {
      console.warn(
        '[relationship] AddRelationDrawer: targetType non registrato nel registry',
        { targetType }
      );
    }
  }, [sourceType, targetType, sourceFqn, targetFqn]);

  // Ricerca server-side paginata: `fetchPage` chiude sopra entityType+groupId.
  // Ritorna payload raw; la label viene risolta sincrona dopo via
  // getLabelFromPayload. Page 1 al mount, load-more on scroll-bottom.
  const fetchPage = useCallback(
    async (q: string, page: number, pageSize: number) => {
      if (!entityType?.search) return { items: [] as Record<string, unknown>[], totalCount: 0 };
      const result = await entityType.search(q, groupId, { page, pageSize });
      return { items: result.items, totalCount: result.totalCount };
    },
    [entityType, groupId]
  );

  const {
    items: rawEntities,
    totalCount,
    loading,
    query: searchQuery,
    setQuery: setSearchQuery,
    loadMore,
    hasMore,
  } = useServerPagedSearch<Record<string, unknown>>({
    fetchPage,
    pageSize: PICKER_PAGE_SIZE,
    enabled: !!entityType?.search,
    // Reset della ricerca quando cambia target o si aggiornano le relazioni
    // esistenti (i filterati dipendono da linkedIdsJson).
    resetOn: [targetType, groupId, linkedIdsJson],
  });

  // Risolve le label sincrone (getLabelFromPayload) o fallback a getById.
  // Filtra out source (solo se stesso tipo) e gia'-linkati.
  const [candidates, setCandidates] = useState<SearchResult[]>([]);
  useEffect(() => {
    let cancelled = false;
    const linkedIds = new Set<number>(JSON.parse(linkedIdsJson));
    // Self-relation guard: ha senso solo se source e target sono dello STESSO
    // tipo (es. Person→Person: l'utente non deve linkare A a se stessa). Per
    // tipi diversi (Business→Person, ecc.) l'id numerico è in domini distinti:
    // un personId che coincide con un businessId è un candidate VALIDO, non
    // self-relation. Il bug precedente filtrava erroneamente come self.
    const sameType = sourceType === targetType;
    Promise.all(
      rawEntities.map(async (entity) => {
        const id = extractEntityIdFromPayload(entity, targetType);
        const syncLabel = getEntityLabelFromPayload(targetType, entity);
        const label = syncLabel ?? (await resolveEntityLabel(targetType, id));
        const description = getEntityDescriptionFromPayload(targetType, entity);
        return { id, label, description };
      })
    ).then((resolved) => {
      if (cancelled) return;
      setCandidates(resolved.filter((r) => {
        if (sameType && r.id === sourceId) return false;
        return !linkedIds.has(r.id);
      }));
    });
    return () => { cancelled = true; };
  }, [rawEntities, targetType, sourceType, sourceId, linkedIdsJson]);

  // Handler scroll: trigger load-more quando il bottom della lista e'
  // vicino al viewport dello scroll container.
  const handleScroll = useCallback((e: UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_THRESHOLD) {
      loadMore();
    }
  }, [loadMore]);

  // Auto-fill: se la lista renderizzata non supera l'altezza del
  // container (es. filtri client-side "mangiano" la maggior parte dei
  // record ricevuti), non ci sara' mai uno scroll event e loadMore
  // non viene triggerato. `useScrollAutoFill` copre il caso richiamando
  // `loadMore` finche' appare uno scroll utile.
  const listRef = useRef<HTMLDivElement | null>(null);
  useScrollAutoFill(listRef, { hasMore, loading, loadMore, deps: [candidates] });

  // Fallback: nessuna ricerca configurata per questo tipo.
  const searchDisabled = !entityType?.search;

  function toggleSelection(id: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  /**
   * Collegamento bidirezionale A<->B. Usato sia dal tab "Aggiungi
   * esistente" sia dal tab "Crea nuova" (dopo aver creato l'entita').
   */
  async function linkWith(newId: number) {
    if (!sourceFqn || !targetFqn) return;
    await dispatch(createBidirectionalRelationship({
      className1: sourceFqn,
      classPK1: sourceId,
      className2: targetFqn,
      classPK2: newId,
      descriptionForward: descForward.trim() || null,
      descriptionReverse: descReverse.trim() || null,
    })).unwrap();
    if (onAfterLink) await onAfterLink(newId);
  }

  async function handleLinkExisting() {
    if (selectedIds.size === 0) return;
    setCreating(true);
    try {
      if (onSelect) {
        // Selettore: collega chi ha passato la callback, con i suoi controlli.
        await onSelect([...selectedIds]);
      } else {
        // Collega sequenzialmente: in caso di errore su una, le successive
        // vengono interrotte. Il thunk createBidirectionalRelationship
        // gestisce gia' internamente il rollback per singola coppia.
        for (const id of selectedIds) {
          await linkWith(id);
        }
      }
      toast({ severity: 'success', message: t('common.saved') });
      onCreated();
    } catch (err) {
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
    } finally {
      setCreating(false);
    }
  }

  async function handleCreateAndLinkPerson(payload: PersonCreatePayload) {
    setCreating(true);
    try {
      const created = await dispatch(createPerson(payload)).unwrap();
      await linkWith(created.personId);
      toast({ severity: 'success', message: t('common.saved') });
      onCreated();
    } catch (err) {
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
    } finally { setCreating(false); }
  }

  async function handleCreateAndLinkBusiness(payload: BusinessCreatePayload) {
    setCreating(true);
    try {
      const created = await dispatch(createBusiness(payload)).unwrap();
      await linkWith(created.businessId);
      toast({ severity: 'success', message: t('common.saved') });
      onCreated();
    } catch (err) {
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
    } finally { setCreating(false); }
  }

  const panelTitle = t('relationship.linkEntity', { entity: entityTypeLabel(entityType, t, targetType) });

  // Le TextField di descrizione sono condivise tra i due tab: compaiono
  // sotto il form "crea nuova" (sempre) e sotto la selezione "esistente"
  // (solo quando un'entita' e' stata selezionata).
  const descriptionFields = (
    <>
      <TextField
        label={t('relationship.descForward', { target: entityTypeLabel(entityType, t, targetType) })}
        value={descForward}
        onChange={(e) => setDescForward(e.target.value)}
        placeholder={t('relationship.descForwardPlaceholder')}
        fullWidth
        className="form-field"
      />
      <TextField
        label={t('relationship.descReverse', { target: entityTypeLabel(entityType, t, targetType) })}
        value={descReverse}
        onChange={(e) => setDescReverse(e.target.value)}
        placeholder={t('relationship.descReversePlaceholder')}
        fullWidth
        className="form-field"
      />
    </>
  );

  // Footer dinamico in base al mode: uniforme agli altri drawer (TaskForm,
  // PersonForm, BusinessForm) — bottoni fissi in basso tramite SidePanel.
  const footer = mode === 'existing' ? (
    <>
      <Button type="button" onClick={onClose} variant="outlined">
        {t('common.cancel')}
      </Button>
      <Button
        type="button"
        onClick={handleLinkExisting}
        variant="contained"
        disabled={creating || selectedIds.size === 0}
      >
        {creating
          ? t('relationship.linking')
          : `${t('relationship.link')} (${selectedIds.size})`}
      </Button>
    </>
  ) : (
    <>
      <Button type="button" onClick={onClose} variant="outlined">
        {t('common.cancel')}
      </Button>
      <Button
        type="submit"
        form={FORM_ID}
        variant="contained"
        disabled={creating || (targetType === 'Person' && personBlocked)}
      >
        {creating ? t('relationship.linking') : t('relationship.createAndLink')}
      </Button>
    </>
  );

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth className="app-dialog">
      {/* Header chiaro con kicker, come le altre modali del restyle. */}
      <DialogTitle className="tc-head">
        <span className="tc-kicker">{panelTitle}</span>
        <IconButton aria-label={t('common.cancel')} onClick={onClose} size="small" className="tc-x">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent className="app-dialog__body">
      {showTabs && (
        <Tabs
          value={mode}
          onChange={(_, v) => {
            // Tab finto: per i target non creabili inline, "Crea" naviga via
            // (non cambia mode, il drawer resta in "esistente" finche' la
            // navigazione non lo smonta).
            if (v === 'new' && fakeCreateTab) {
              onCreateNew?.();
              return;
            }
            setMode(v as Mode);
          }}
          variant="fullWidth"
        >
          <Tab value="existing" label={t('relationship.tabAddExisting')} className="tab-lowercase" />
          <Tab value="new" label={t('relationship.tabCreateNew')} className="tab-lowercase" />
        </Tabs>
      )}

      {mode === 'existing' ? (
        <>
          <TextField
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('relationship.searchEntity', { entity: entityTypeLabel(entityType, t, targetType) })}
            size="small"
            fullWidth
            className="form-field"
            autoFocus
            disabled={searchDisabled}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
          <div className="text-xs text-muted mb-sm">
            {loading && candidates.length === 0
              ? t('common.loading')
              : t('relationship.candidatesCount', {
                  // Singolare e plurale insieme: i18next sceglie la variante col numero (_one /
                  // _other), e la variante sceglie la forma. «1 Contacts» era il difetto.
                  count: candidates.length,
                  singular: entityTypeLabel(entityType, t, targetType),
                  plural: entityTypeLabelPlural(entityType, t, targetType),
                })}
          </div>
          <div className="entity-picker-list" onScroll={handleScroll} ref={listRef}>
            {candidates.length === 0 && !loading && (
              <p className="text-muted text-sm p-sm">{t('relationship.noSearchResults')}</p>
            )}
            {candidates.map((c) => (
              <label key={c.id} className="entity-picker-list__row">
                <Checkbox
                  size="small"
                  checked={selectedIds.has(c.id)}
                  onChange={() => toggleSelection(c.id)}
                />
                <span className="entity-picker-list__label">
                  {c.label}
                  {c.description && (
                    <span className="entity-picker-list__description">{c.description}</span>
                  )}
                </span>
              </label>
            ))}
            {loading && candidates.length > 0 && (
              <p className="text-muted text-xs p-sm text-center">{t('common.loading')}</p>
            )}
            {!loading && !hasMore && candidates.length > 0 && totalCount > PICKER_PAGE_SIZE && (
              <p className="text-muted text-xs p-sm text-center">{t('common.endOfList')}</p>
            )}
          </div>

          {selectedIds.size > 0 && !onSelect && descriptionFields}
        </>
      ) : (
        <>
          {/* Il form body ha id={FORM_ID}: il bottone submit del footer
              del Dialog ha form={FORM_ID} per triggerare il submit. */}
          {targetType === 'Person' && (
            <PersonFormBody
              formId={FORM_ID}
              onSubmit={handleCreateAndLinkPerson}
              onBlockingChange={setPersonBlocked}
            />
          )}
          {targetType === 'Business' && (
            <BusinessFormBody
              formId={FORM_ID}
              onSubmit={handleCreateAndLinkBusiness}
            />
          )}
          {/* Descrizioni della relazione in fondo al form body
              (i campi vivono fuori dal <form id={FORM_ID}> ma in fondo
              al panel per essere visivamente l'ultima cosa prima del
              footer Salva/Annulla). */}
          {descriptionFields}
        </>
      )}
      </DialogContent>
      <DialogActions className="app-dialog__footer">{footer}</DialogActions>
    </Dialog>
  );
}
